import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Clock,
  HelpCircle,
  DollarSign,
  Users,
  Rocket,
  Tag,
  CheckCircle,
  CreditCard,
  Banknote,
  Receipt,
  X,
  Check
} from 'lucide-react';

const Pricing = () => {
  // Anticipated plan tiers
  const anticipatedPlans = [
    {
      name: "Influencer",
      description: "For content creators looking to connect with brands",
      features: [
        "Create a professional profile",
        "Apply to brand campaigns",
        "Receive product samples",
        "Get paid for authentic reviews",
        "Analytics dashboard",
        "Portfolio showcase"
      ],
      icon: <Users className="h-8 w-8 text-primary" />,
      comingSoon: true
    },
    {
      name: "Brand Starter",
      description: "For small to medium businesses new to influencer marketing",
      features: [
        "Launch review campaigns",
        "Connect with relevant influencers",
        "Basic analytics and reporting",
        "Content rights management",
        "Campaign performance tracking",
        "Email support"
      ],
      icon: <Rocket className="h-8 w-8 text-primary" />,
      comingSoon: true
    },
    {
      name: "Brand Growth",
      description: "For established brands looking to scale their influencer marketing",
      features: [
        "Everything in Starter",
        "Advanced campaign analytics",
        "Influencer relationship management",
        "Priority campaign approval",
        "Dedicated account manager",
        "Custom campaign strategies"
      ],
      icon: <Tag className="h-8 w-8 text-primary" />,
      comingSoon: true
    }
  ];

  // FAQ items
  const faqs = [
    {
      question: "When will pricing be available?",
      answer: "We're currently in the pre-launch phase and finalizing our pricing structure. Sign up for our early access program to be notified when pricing is available and to potentially qualify for special launch discounts."
    },
    {
      question: "Will there be a free tier?",
      answer: "Yes, we plan to offer a free tier for influencers to create profiles and apply to a limited number of campaigns each month. Brands will have access to a free trial period to explore our platform."
    },
    {
      question: "How will the pricing structure work?",
      answer: "Our pricing will be tiered based on usage and features. For brands, pricing will depend on the number of campaigns, influencer reach, and additional services. For influencers, most features will be free with optional premium upgrades."
    },
    {
      question: "Will there be any setup or onboarding fees?",
      answer: "No, we don't plan to charge any setup fees. Our goal is transparent pricing with no hidden costs. Premium onboarding services may be available for enterprise clients."
    },
    {
      question: "Can I get early access pricing?",
      answer: "Absolutely! Join our early access program to lock in special founder rates that will be grandfathered in even after our official launch. Early adopters will receive significant discounts on our regular pricing."
    },
    {
      question: "Do you offer a free trial?",
      answer: "We offer a 14-day free trial for new customers on our Basic and Professional plans. No credit card is required to start your trial."
    }
  ];

  return (
    <div className="bg-white min-h-screen relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-15"></div>
        
        {/* Pricing-related floating icons */}
        {[DollarSign, CreditCard, Banknote, Receipt, Tag].map((Icon, i) => (
          <motion.div
            key={`pricing-icon-${i}`}
            className="absolute text-primary opacity-20"
            style={{
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 90 + 5}%`,
              fontSize: `${Math.random() * 1 + 0.8}rem`
            }}
            animate={{
              y: [0, Math.random() * 30 - 15],
              rotate: [0, Math.random() * 20 - 10]
            }}
            transition={{
              duration: Math.random() * 8 + 7,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Icon className="w-12 h-12" />
          </motion.div>
        ))}
        
        {/* Decorative circles */}
        <motion.div 
          className="absolute rounded-full bg-primary/10 w-96 h-96 -right-20 top-1/4"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-blue-400/10 w-64 h-64 -left-10 top-2/3"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Currency symbols */}
        {['₱', '$', '%', '€', '£'].map((symbol, i) => (
          <motion.div
            key={`currency-symbol-${i}`}
            className="absolute text-primary opacity-15 font-bold"
            style={{
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 90 + 5}%`,
              fontSize: `${Math.random() * 24 + 16}px`
            }}
            animate={{
              y: [0, Math.random() * 40 - 20],
              opacity: [0.15, 0.25, 0.15]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            {symbol}
          </motion.div>
        ))}
        
        {/* Pricing tier dots */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={`pricing-dot-${i}`}
            className="absolute rounded-full bg-primary"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.2
            }}
            animate={{
              x: [0, Math.random() * 40 - 20],
              y: [0, Math.random() * 40 - 20],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
        
        {/* Pricing comparison lines */}
        {[...Array(6)].map((_, i) => (
          <motion.div 
            key={`pricing-line-${i}`}
            className="absolute bg-primary h-px opacity-20"
            style={{
              width: `${Math.random() * 30 + 10}%`,
              left: `${Math.random() * 90}%`,
              top: `${Math.random() * 100}%`,
              transformOrigin: 'left',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.2
            }}
          />
        ))}
      </div>
      <Header />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-blue-700 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-4">
              <Clock className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
              Coming Soon
            </span>
            <h1 className="text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
              Pricing Coming Soon
            </h1>
            <p className="mt-3 max-w-2xl mx-auto text-base text-blue-100 sm:text-lg md:mt-5 md:text-xl">
              We're finalizing our pricing plans to offer the best value for brands and influencers
            </p>
            <div className="mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              >
                Get Notified When Pricing Launches
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Anticipated Plans Section */}
      <div className="pt-24 pb-16 bg-white relative overflow-hidden">
        {/* Pricing section background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Pricing tiers visualization */}
          <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="pricing-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <rect x="10" y="10" width="20" height="20" fill="none" stroke="#3B5BA5" strokeWidth="1" />
                <rect x="40" y="40" width="30" height="30" fill="none" stroke="#3B5BA5" strokeWidth="1" />
                <rect x="80" y="10" width="10" height="10" fill="none" stroke="#3B5BA5" strokeWidth="1" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#pricing-pattern)" />
          </svg>
          
          {/* Animated price tags */}
          {[...Array(3)].map((_, i) => (
            <motion.div 
              key={`price-tag-${i}`}
              className="absolute flex items-center justify-center rounded-md border-2 border-primary bg-white/5"
              style={{
                width: 80,
                height: 40,
                left: `${25 + i * 25}%`,
                top: `${20}%`,
                opacity: 0.2,
              }}
              animate={{
                y: [0, 10, 0],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.5
              }}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">Anticipated Plan Tiers</h2>
            <p className="mt-4 text-lg text-gray-500">Here's a preview of what our pricing plans will include when they launch</p>
          </div>
          <div className="mt-12 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8">
            {anticipatedPlans.map((plan, index) => (
              <motion.div 
                key={plan.name}
                className="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                {plan.comingSoon && (
                  <div className="absolute top-0 right-6 -mt-3 bg-blue-100 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wide text-blue-800">
                    Coming Soon
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-center h-16 w-16 rounded-md bg-primary-50 text-primary mb-4">
                    {plan.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                  <p className="mt-6 text-gray-500">{plan.description}</p>
                  
                  {/* Feature list */}
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature, idx) => (
                      <li key={`${plan.name}-feature-${idx}`} className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <p className="ml-3 text-base text-gray-500">{feature}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-8">
                  <button
                    className="w-full bg-blue-50 text-primary hover:bg-blue-100 border border-blue-200 rounded-md px-5 py-3 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                  >
                    Join Waitlist
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Feature Comparison */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Compare Plans</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Find the Right Plan for Your Business
            </p>
          </motion.div>
          
          <motion.div 
            className="mt-10 overflow-x-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    Feature
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                    Basic
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                    Professional
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Active campaigns</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Up to 3</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Up to 10</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Influencer tiers</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Nano, Micro</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Nano, Micro, Mid-tier</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">All tiers</td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Analytics</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Basic</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Advanced</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Enterprise</td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Support</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Email</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Email & Chat</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">24/7 Priority</td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Campaign approval</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">48 hours</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">24 hours</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500">Expedited</td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Account manager</td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <X className="h-5 w-5 text-gray-400 mx-auto" />
                  </td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Custom strategy</td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <X className="h-5 w-5 text-gray-400 mx-auto" />
                  </td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <X className="h-5 w-5 text-gray-400 mx-auto" />
                  </td>
                  <td className="px-3 py-4 text-center text-gray-500">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </p>
          </motion.div>
          
          <div className="mt-12 max-w-3xl mx-auto">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {faqs.map((faq, index) => (
                <motion.div 
                  key={faq.question}
                  className="bg-white shadow overflow-hidden rounded-md"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                >
                  <div className="px-6 py-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    <div className="mt-2">
                      <p className="text-base text-gray-500">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Custom Pricing CTA */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="bg-primary rounded-2xl shadow-xl overflow-hidden lg:grid lg:grid-cols-2 lg:gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="pt-10 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:pr-0 xl:py-20 xl:px-20">
              <div className="lg:self-center">
                <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                  <span className="block">Need a custom solution?</span>
                  <span className="block">Let's talk.</span>
                </h2>
                <p className="mt-4 text-lg leading-6 text-blue-100">
                  Our team can create a tailored plan that perfectly fits your brand's unique needs and goals.
                </p>
                <motion.div 
                  className="mt-8"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-primary bg-white hover:bg-gray-50 transition-colors duration-300"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </div>
            <div className="relative -mt-6 aspect-w-5 aspect-h-3 md:aspect-w-2 md:aspect-h-1">
              <img
                className="transform translate-x-6 translate-y-6 rounded-md object-cover object-left-top sm:translate-x-16 lg:translate-y-20"
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?ixlib=rb-1.2.1&auto=format&fit=crop&w=2830&q=80&sat=-100"
                alt="App screenshot"
              />
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Money-back guarantee */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <HelpCircle className="h-12 w-12 text-primary mx-auto" />
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-gray-900">
              Still have questions?
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-base text-gray-500">
              Our team is ready to answer any questions you have about our pricing or features.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="text-base font-medium text-primary hover:text-primary-dark"
              >
                Contact our sales team <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Pricing;
